import React, { useState } from 'react';
import useApi from '../../hooks/useApi';
import haxios from '../../lib/haxios';
import {
    ArrowUpTrayIcon,
    TrashIcon,
    EyeIcon,
    ArrowDownTrayIcon,
    DocumentIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function DocumentsTab({ vehicleId }) {
    const { data: documentsResponse, refetch, isLoading } = useApi({ url: `/visiondashboard/salesassist/vehicles/${vehicleId}/documents`, initial: { data: [] } });
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    // Get the documents from the response data structure
    const documents = documentsResponse?.data || [];

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    // Function to get file type icon based on extension
    const getFileIcon = (filename) => {
        const extension = filename.split('.').pop().toLowerCase();
        // You could add more conditions for different file types
        if (['pdf'].includes(extension)) {
            return <DocumentIcon className="w-8 h-8 text-red-500" />;
        } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
            return <DocumentIcon className="w-8 h-8 text-blue-500" />;
        } else if (['doc', 'docx'].includes(extension)) {
            return <DocumentIcon className="w-8 h-8 text-blue-700" />;
        } else if (['xls', 'xlsx'].includes(extension)) {
            return <DocumentIcon className="w-8 h-8 text-green-600" />;
        }
        return <DocumentIcon className="w-8 h-8 text-gray-500" />;
    };

    // Format the filename to show just the original name without timestamp prefix
    const formatFilename = (filename) => {
        // If the filename contains a timestamp pattern (e.g., 1742154776431-5p6qa6ofr.pdf)
        if (filename && filename.includes('-')) {
            const parts = filename.split('-');
            if (parts.length > 1 && !isNaN(parts[0])) {
                // Return everything after the first dash
                return parts.slice(1).join('-');
            }
        }
        return filename;
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setIsUploading(true);
        console.log('Uploading file:', selectedFile.name);

        const formData = new FormData();
        formData.append('document', selectedFile); // Match field name in multer

        try {
            // haxios already adds the /api prefix, so don't include it here
            const uploadUrl = `/visiondashboard/salesassist/vehicles/${vehicleId}/documents/upload`;
            console.log('Making API request to:', uploadUrl);

            // Use haxios for post requests
            const response = await haxios.post(uploadUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            console.log('Upload response:', response);

            // Show success notification
            setUploadSuccess(true);
            setTimeout(() => setUploadSuccess(false), 3000); // Hide after 3 seconds

            setSelectedFile(null);
            refetch(); // Refresh the documents list after successful upload
        } catch (error) {
            console.error('Error uploading document:', error);
            alert('Failed to upload document: ' + (error.message || 'Unknown error'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (docId) => {
        try {
            // haxios already adds the /api prefix
            const response = await haxios.delete(`/visiondashboard/salesassist/vehicles/${vehicleId}/documents/${docId}`);

            console.log('Delete response:', response);

            if (response.success) {
                refetch();
            } else {
                throw new Error(response.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Failed to delete document: ' + (error.message || 'Unknown error'));
        }
    };

    return (
        <div className="p-4">
            <h2 className="text-lg font-semibold mb-4">Vehicle Documents</h2>

            {/* Upload Form */}
            <div className="border p-4 rounded-lg bg-gray-100 mb-6">
                <div className="mb-3">
                    <input
                        type="file"
                        onChange={handleFileChange}
                        className="mb-2 w-full"
                        id="document-upload"
                    />
                    <button
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md flex items-center"
                        onClick={handleUpload}
                        disabled={!selectedFile || isUploading}
                        type="button"
                    >
                        <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                        {isUploading ? 'Uploading...' : 'Upload'}
                    </button>
                </div>

                {selectedFile && (
                    <div className="mt-2 text-sm text-gray-700">
                        Selected file: {selectedFile.name}
                    </div>
                )}

                {uploadSuccess && (
                    <div className="mt-2 p-2 bg-green-100 text-green-800 rounded-md flex items-center">
                        <CheckCircleIcon className="w-5 h-5 mr-2" />
                        Document uploaded successfully!
                    </div>
                )}
            </div>

            {/* Documents List */}
            {isLoading ? (
                <div className="p-4 text-center">
                    <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading documents...</p>
                </div>
            ) : (
                <>
                    <h3 className="text-md font-medium mb-3">Document Library</h3>

                    {documents.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {documents.map((doc) => (
                                <div key={doc.id} className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow">
                                    <div className="flex items-center mb-3">
                                        {getFileIcon(doc.filename || doc.name || '')}
                                        <div className="ml-3 overflow-hidden">
                                            <p className="font-medium text-gray-800 truncate">
                                                {formatFilename(doc.filename || doc.name)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {doc.size && (
                                                    <span className="mr-2">
                                                        {Math.round(doc.size / 1024)} KB
                                                    </span>
                                                )}
                                                {new Date(doc.created_at || Date.now()).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-2 mt-2">
                                        <button className="p-1 rounded-full hover:bg-blue-100" title="View Document">
                                            <a href={`https://vcp.connectplus.parts/api/public/uploads/vehicles/${vehicleId}/${doc.filename}`} target="_blank" rel="noopener noreferrer">
                                                <EyeIcon className="w-5 h-5 text-blue-600" />
                                            </a>
                                        </button>


                                        <button
                                            className="p-1 rounded-full hover:bg-red-100"
                                            title="Delete Document"
                                            onClick={() => handleDelete(doc.id)}
                                        >
                                            <TrashIcon className="w-5 h-5 text-red-600" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                            <DocumentIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500">No documents uploaded yet.</p>
                            <p className="text-gray-400 text-sm mt-1">Upload a document to get started.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}